import React, { useEffect, useMemo } from 'react';

import { Button } from 'design-system-web';
import { Alert} from 'components/lds';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation, Trans } from 'react-i18next';

import { Flex} from 'components';
import { WhiteLabelType } from 'application-types';

import { useSwireApp } from 'react-hooks';
import { usePhoneNumberExists } from 'lane-shared/hooks/auth/usePhoneNumberExists';
import { ProfileData } from 'lane-shared/contexts/OnboardingContext';
import { HookFormInput, HookFormDropdown } from 'components/reactHookForm';
import { MarketingEmailOptIn } from '../MarketingEmailOptIn';
import { TermsAndConditionsOptIn } from '../TermsAndConditionsOptIn';
import { ProfilePhoneNumberInput } from '../../components/ProfilePhoneNumberInput';

import styles from './index.scss';
import { useFlag } from 'lane-shared/hooks';
import { FeatureFlag } from 'constants-flags';
import { ALLOWED_CHARACTERS_PATTERN } from 'constants-activate';

import { SwireHongKongTnC } from '../SwireHongKongTnC';
import { SwireOtherTnC } from '../SwireOtherTnC';

const TRANSLATION_KEYS = {
  fullNameLabel: 'web.onboarding.userProfile.fullNameLabel',
  firstNameLabel: 'web.onboarding.userProfile.firstNameLabel',
  lastNameLabel: 'web.onboarding.userProfile.lastNameLabel',
  locationLabel: 'web.onboarding.userProfile.locationLabel',
  locationPlaceholder: 'web.onboarding.userProfile.locationPlaceholder',
  button: 'web.onboarding.userProfile.continueButton',
  requiredFieldError: 'web.onboarding.userProfile.requiredField.error',
  textLengthError: 'web.onboarding.userProfile.textLength.error',
  invalidCharactersError: 'web.onboarding.userProfile.invalidCharacters.error',
  requiredIndicatorInfo: 'web.onboarding.userProfile.requiredIndicatorInfo',
};

enum LocationOptionsEnum {
  HONG_KONG_SAR = "hong-kong-sar",
  OTHER = "other",
}

const DEFAULT_SWIRE_CONSENT_VALUES = {
  commonTnC: false,
  hkTnC: false,
  otherTnC1: false,
  otherTnC2: false,
  otherTnC3: false,
  otherTnC4: false,
};

export type FormData = {
  fullName: string;
  firstName: string;
  lastName: string;
  phone: string;
  location?: string; // Optional since it's only in the new onboarding flow
  marketingOptIn: boolean;
  termsAndConditionsOptIn: boolean;
  swire: {
    commonTnC: boolean;
    hkTnC: boolean;
    otherTnC1: boolean;
    otherTnC2: boolean;
    otherTnC3: boolean;
    otherTnC4: boolean;
  };
};



type Props = {
  defaultUserName?: string;
  defaultFirstName?: string;
  defaultLastName?: string;
  defaultPhone?: string;
  verifiedPhone?: string;
  defaultLocation?: string;
  onSubmit: (data: ProfileData) => void;
  whitelabel: Pick<
    WhiteLabelType,
    | 'termsAndConditionsUrls'
    | 'privacyPolicyUrls'
    | 'complianceContact'
    | 'name'
    | 'settings'
    | 'url'
  >;
};

const allowCharactersPattern = ALLOWED_CHARACTERS_PATTERN;

export const ProfileForm = ({
  defaultUserName,
  defaultFirstName,
  defaultLastName,
  defaultPhone,
  verifiedPhone,
  defaultLocation,
  onSubmit,
  whitelabel,
}: Props) => {
  const { t } = useTranslation();

  const isNewOnboardingFeatureEnabled = useFlag(
    FeatureFlag.OnboardingProfileFirstAndLastName,
    false
  );

  const { isSwire: isSwireDomain } = useSwireApp(whitelabel);

  const isSwireSignupFlow = useFlag(FeatureFlag.EnableSwireSignupFlow, false);
  const isPhoneNumberReqForSignup = useFlag(FeatureFlag.PhoneNumberReqForSignup, false);
  const isSavePhoneVerificationEnabled = useFlag(
    FeatureFlag.SavePhoneVerificationDuringOnboarding,
    false
  );

  // Phone number existence check hook
  const {
    checkPhoneNumberExists,
    error: phoneValidationError,
    isLoading: isCheckingPhone,
    phoneNumberExists
  } = usePhoneNumberExists();

  const {
    handleSubmit,
    control,
    setValue,
    getValues,
    formState: { isValid, errors },
  } = useForm<FormData>({
    mode: 'all',
    defaultValues: {
      fullName: defaultUserName || '',
      firstName: defaultFirstName ?? '',
      lastName: defaultLastName ?? '',
      phone: defaultPhone ?? '',
      location: defaultLocation,
      marketingOptIn: false,
      termsAndConditionsOptIn: false,
      swire: DEFAULT_SWIRE_CONSENT_VALUES,
    },
  });

  // Watch the location field to dynamically add class for floating label
  const selectedLocation = useWatch({
    control,
    name: 'location',
  });

  const LOCATION_OPTIONS = useMemo(
    () => [
      {
        label: t('web.onboarding.userProfile.locationOptions.hongKongSAR'),
        value: LocationOptionsEnum.HONG_KONG_SAR,
      },
      {
        label: t('web.onboarding.userProfile.locationOptions.other'),
        value: LocationOptionsEnum.OTHER,
      },
    ],
    [t]
  );

  // Extract actual value from the object (HookFormDropdown may return an object or string)
  const locationValue =
    typeof selectedLocation === 'object' &&
    selectedLocation !== null &&
    'value' in selectedLocation
      ? (selectedLocation as { value: string }).value
      : selectedLocation;



  useEffect(() => {
    if(locationValue) {
      setValue('swire', DEFAULT_SWIRE_CONSENT_VALUES);
      setValue('marketingOptIn', false);
    }
  }, [locationValue, setValue]);

  useEffect(() => {
    if (isSavePhoneVerificationEnabled && defaultPhone && !getValues('phone')) {
      setValue('phone', defaultPhone, { shouldValidate: true });
    }
  }, [defaultPhone, isSavePhoneVerificationEnabled, setValue, getValues]);

  const getErrorMessage = (field: keyof FormData) => {
    const fieldError = errors[field];

    if (fieldError?.type === 'required') {
      return [t(TRANSLATION_KEYS.requiredFieldError)];
    }

    if (fieldError?.type === 'minLength') {
      return [t(TRANSLATION_KEYS.textLengthError)];
    }

    if (fieldError?.type === 'pattern') {
      return [t(TRANSLATION_KEYS.invalidCharactersError)];
    }

    return null;
  };

  const handleFormSubmission = async (data: FormData) => {
    const fullName = isNewOnboardingFeatureEnabled
      ? `${data.firstName.trim()} ${data.lastName.trim()}`
      : data.fullName.trim();

    // Check phone number existence if required
    if (isPhoneNumberReqForSignup && data.phone) {
      const isOwnPrefilledPhone =
        isSavePhoneVerificationEnabled && !!verifiedPhone && data.phone === verifiedPhone;

      if (!isOwnPrefilledPhone) {
        try {
          const phoneIsValid = await checkPhoneNumberExists(data.phone);

          if (!phoneIsValid) {
            return;
          }
        } catch (error) {
          console.error('Phone validation failed:', error);

          return;
        }
      }
    }

    // Regular submission
    onSubmit({
      fullName,
      phone: data.phone,
      location: locationValue,
      marketingOptIn: data.marketingOptIn,
    });
  };


  return (
    <form
      className={styles.profileForm}
      onSubmit={handleSubmit(handleFormSubmission)}
    >
       {phoneValidationError && phoneNumberExists === null && <Flex direction="row" className={styles.alertContainer}>
          <Alert type="error" className={styles.alert}>
            {phoneValidationError}
          </Alert>
        </Flex>
      }
      {isNewOnboardingFeatureEnabled ? (
        <>
          {/* Separate first and last name fields */}
          <HookFormInput
            className={styles.nameInput}
            control={control}
            isRequired
            name="firstName"
            label={TRANSLATION_KEYS.firstNameLabel}
            errorMessage={getErrorMessage('firstName')}
            pattern={{ value: allowCharactersPattern }}
          />

          <HookFormInput
            className={styles.nameInput}
            control={control}
            isRequired
            name="lastName"
            label={TRANSLATION_KEYS.lastNameLabel}
            errorMessage={getErrorMessage('lastName')}
            pattern={{ value: allowCharactersPattern }}
          />
          {/* Swire Signup Flow for Web */}
          {isPhoneNumberReqForSignup && (
            <ProfilePhoneNumberInput
              control={control}
              whitelabel={whitelabel}
              getErrorMessage={getErrorMessage}
              phoneValidationError={(phoneNumberExists && phoneValidationError) || '' }
            />
          )}

          {isSwireDomain && isSwireSignupFlow && (
            <div className={styles.locationInput} data-test="onboarding-location-dropdown-container">
              <span
                className={`${styles.floatingLabel} ${locationValue ? styles.hasValue : ''}`}
              >
                {t(TRANSLATION_KEYS.locationLabel)}               
              </span>
              <HookFormDropdown
                control={control}
                name="location"
                items={LOCATION_OPTIONS}
                placeholder=""
                isFullWidth
                testId="onboarding-location-dropdown"
                doTranslation={false}
                isRequired
              />
            </div>
          )}
        </>
      ) : (
        // Single full name field
        <HookFormInput
          className={styles.nameInput}
          control={control}
          isRequired
          name="fullName"
          label={TRANSLATION_KEYS.fullNameLabel}
          minLength={2}
          errorMessage={getErrorMessage('fullName')}
        />
      )}

      {isSwireSignupFlow && isSwireDomain ? (
        locationValue && <>  
         <div className={styles.requiredLabel}>
            <Trans
              i18nKey={TRANSLATION_KEYS.requiredIndicatorInfo}
              components={[
                <span key="requiredIndicator" className={styles.requiredIndicator}>*</span>,
              ]}
            />
          </div>
       
        <div className={styles.swireTnCScrollContainer}>
          {/* Swire Signup Flow for Web */}
          {locationValue === LocationOptionsEnum.OTHER && (
            // Single scrollable container for all T&C components when location is "other"

            <div className={styles.termsAndConditions}>
              <SwireOtherTnC
                control={control}
                whitelabel={whitelabel}
              />
            </div>
          )}
          {locationValue === LocationOptionsEnum.HONG_KONG_SAR && (
            // Separate containers for other locations

            <div className={styles.termsAndConditions}>
              <SwireHongKongTnC
                control={control}
                whitelabel={whitelabel}
              />
            </div>
          )}
        </div>
        </>
      ) : (
        <>
          <div className={styles.termsAndConditions}>
            <TermsAndConditionsOptIn
              control={control}
              whitelabel={{
                name: whitelabel.name,
                termsAndConditionsUrls: whitelabel.termsAndConditionsUrls,
                privacyPolicyUrls: whitelabel.privacyPolicyUrls,
              }}
            />
          </div>
          <div className={styles.marketingEmail}>
            <MarketingEmailOptIn
              control={control}
              complianceContact={whitelabel?.complianceContact}
            />
          </div>
        </>
      )}
      <Button
        className={styles.button}
        fullWidth
        size="large"
        type="submit"
        disabled={!isValid || isCheckingPhone}
      >
        {t(TRANSLATION_KEYS.button)}
      </Button>
    </form>
  );
};
